import { apiClient } from '../utils/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  organizationId?: string | null;
  role?: string | null;             // 'super_admin' | 'admin' | 'viewer' | null
  permissions?: string[];           // role-derived permission keys
}

export interface Organization {
  id: string;
  name: string;
  plan: string;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: User;
    organization?: Organization | null;
    accessToken: string;
    refreshToken: string;
  };
  message: string;
  timestamp: string;
  statusCode: number;
}

export interface RefreshTokenResponse {
  success: boolean;
  data: {
    accessToken: string;
  };
  message: string;
  timestamp: string;
  statusCode: number;
}

export interface RequestPasswordResetResponse {
  success: boolean;
  data: {
    requested: boolean;
  };
  message: string;
  timestamp: string;
  statusCode: number;
}

export interface ResetPasswordResponse {
  success: boolean;
  data: {
    reset: boolean;
  };
  message: string;
  timestamp: string;
  statusCode: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RequestPasswordResetRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  otp: string;
  newPassword: string;
}

export interface UpdateCompanyRequest {
  companyName: string;
}

export interface UpdateCompanyResponse {
  success: boolean;
  data: {
    user: User;
  };
  message: string;
  timestamp: string;
  statusCode: number;
}

export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdatePasswordResponse {
  success: boolean;
  data: {
    updated: boolean;
  };
  message: string;
  timestamp: string;
  statusCode: number;
}

class AuthService {
  private readonly TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly USER_KEY = 'user';
  private readonly ORGANIZATION_KEY = 'organization';

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/auth/register', data);
    this.setAuthData(response);
    return response;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/auth/login', data);
    this.setAuthData(response);
    return response;
  }

  async refreshToken(): Promise<RefreshTokenResponse | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await apiClient.post('/api/v1/auth/refresh', { refreshToken });
      if (response.success && response.data.accessToken) {
        this.setAccessToken(response.data.accessToken);
      }
      return response;
    } catch (error) {
      // If refresh token fails, logout user
      this.logout();
      throw error;
    }
  }

  async requestPasswordReset(data: RequestPasswordResetRequest): Promise<RequestPasswordResetResponse> {
    const payload = { email: data.email.trim().toLowerCase() };
    return await apiClient.post('/api/v1/auth/request-password-reset', payload);
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const payload = {
      email: data.email.trim().toLowerCase(),
      otp: data.otp,
      newPassword: data.newPassword,
    };
    return await apiClient.post('/api/v1/auth/reset-password', payload);
  }

  async updateCompanyName(data: UpdateCompanyRequest): Promise<UpdateCompanyResponse> {
    const response = await apiClient.put('/api/v1/account/company', {
      companyName: data.companyName.trim(),
    });
    if (response.success && response.data?.user) {
      const current = this.getCurrentUser();
      // Some account update responses are partial; preserve role/permissions fields.
      this.setUser({ ...(current || {}), ...response.data.user } as User);
    }
    return response;
  }

  async updatePassword(data: UpdatePasswordRequest): Promise<UpdatePasswordResponse> {
    return await apiClient.put('/api/v1/account/password', data);
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.ORGANIZATION_KEY);
  }

  getCurrentOrganization(): Organization | null {
    const raw = localStorage.getItem(this.ORGANIZATION_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getCurrentUser(): User | null {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  private setAuthData(response: AuthResponse): void {
    if (response.success && response.data) {
      this.setAccessToken(response.data.accessToken);
      this.setRefreshToken(response.data.refreshToken);
      this.setUser(response.data.user);
      if (response.data.organization) {
        localStorage.setItem(this.ORGANIZATION_KEY, JSON.stringify(response.data.organization));
      }
    }
  }

  private setAccessToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }
}

const authService = new AuthService();
export default authService;
